// src/components/tasks/SimpleQuickTaskCreate.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Fade,
  Collapse,
  useTheme,
  alpha as muiAlpha,
  Typography,
  InputAdornment,
  Chip,
  ClickAwayListener,
} from '@mui/material';
import {
  Add as AddIcon,
  Send as SendIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Flag as FlagIcon,
  ExpandMore as ExpandIcon,
  KeyboardReturn as EnterIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, addDays, startOfDay } from 'date-fns';
import Grid from '@mui/material/Grid';
import useMediaQuery from '@mui/material/useMediaQuery';

// Import stores
import useTaskStore from '../../stores/taskStore';
import useProjectStore from '../../stores/projectStore';
import useAuthStore from '../../stores/authStore';
import useNotificationStore from '../../stores/notificationStore';

// Design system components
import { Button } from '../../design-system/components/core';

/**
 * SimpleQuickTaskCreate - A clean, minimal task creation component
 * 
 * Features:
 * - Single line input for quick task creation
 * - Press Enter to create
 * - Optional quick actions (assignee, due date, priority)
 * - Progressive disclosure - advanced options only when needed
 * - Smart defaults based on context
 * - Clean, uncluttered UI
 */
const SimpleQuickTaskCreate = ({ 
  projectId,
  sectionId,
  onClose,
  onSuccess,
  placeholder = "What needs to be done?",
  autoFocus = true,
  variant = 'inline', // 'inline', 'floating', 'minimal'
}) => {
  const theme = useTheme();
  const inputRef = useRef(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Safe alpha function wrapper
  const alpha = (color, opacity) => {
    try {
      // Check if color is a valid string
      if (typeof color !== 'string' || !color) {
        console.warn('Invalid color passed to alpha:', color);
        return `rgba(0, 0, 0, ${opacity})`;
      }
      // Use MUI's alpha function
      return muiAlpha(color, opacity);
    } catch (error) {
      console.warn('Error in alpha function:', error, 'Color:', color);
      // Return a fallback transparent color
      return `rgba(0, 0, 0, ${opacity})`;
    }
  };
  
  // State
  const [title, setTitle] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dueDate, setDueDate] = useState(null);
  const [assigneeId, setAssigneeId] = useState(null);
  const [priority, setPriority] = useState('medium');
  const [isCreating, setIsCreating] = useState(false);
  
  // Stores
  const { createTask } = useTaskStore();
  const { currentProject, projects } = useProjectStore();
  const { user } = useAuthStore();
  const { showSuccess, showError } = useNotificationStore();
  
  // Get the project (use provided projectId or current project)
  const project = projectId 
    ? projects.find(p => p._id === projectId) 
    : currentProject;
    
  // Auto-focus on mount
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);
  
  // Handle task creation
  const handleCreate = async () => {
    if (!title.trim()) return;
    
    setIsCreating(true);
    try {
      const taskData = {
        title: title.trim(),
        projectId: projectId || currentProject?._id,
        sectionId: sectionId,
        priority: priority,
        status: 'to_do',
        dueDate: dueDate,
        assignee: assigneeId,
      };
      
      await createTask(taskData);
      
      // Success
      showSuccess('Task created successfully');
      setTitle('');
      setDueDate(null);
      setAssigneeId(null);
      setPriority('medium');
      setIsExpanded(false);
      
      if (onSuccess) {
        onSuccess();
      }
      
      // Keep focus for continuous creation
      if (inputRef.current) {
        inputRef.current.focus();
      }
    } catch (error) {
      showError('Failed to create task');
      console.error('Task creation error:', error);
    } finally {
      setIsCreating(false);
    }
  };
  
  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCreate();
    }
    // Escape to close
    if (e.key === 'Escape' && onClose) {
      onClose();
    }
  };
  
  // Quick date options
  const quickDates = [
    { label: 'Today', value: startOfDay(new Date()) },
    { label: 'Tomorrow', value: addDays(startOfDay(new Date()), 1) },
    { label: 'Next Week', value: addDays(startOfDay(new Date()), 7) },
  ];
  
  // Helper function to safely get color value
  const getColor = (path, fallback) => {
    const parts = path.split('.');
    let value = theme.palette;
    for (const part of parts) {
      value = value?.[part];
      if (!value) return fallback;
    }
    return typeof value === 'string' ? value : fallback;
  };

  // Priority colors - ensure we have actual color values
  const priorityColors = {
    low: getColor('success.main', '#4caf50'),
    medium: getColor('info.main', '#2196f3'),
    high: getColor('warning.main', '#ff9800'),
    urgent: getColor('error.main', '#f44336'),
  };
  
  // Debug: Check if colors are valid hex values
  if (process.env.NODE_ENV === 'development') {
    Object.entries(priorityColors).forEach(([key, value]) => {
      if (typeof value !== 'string' || (!value.match(/^#[0-9A-F]{6}$/i) && !value.match(/^#[0-9A-F]{3}$/i) && !value.match(/^rgb/i))) {
        console.warn(`Invalid color for priority ${key}:`, value);
      }
    });
  }
  
  // Main input field
  const renderMainInput = () => (
    <TextField
      ref={inputRef}
      fullWidth
      value={title}
      onChange={(e) => setTitle(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder={isMobile ? "Add task..." : placeholder}
      variant="standard"
      disabled={isCreating}
      InputProps={{
        disableUnderline: true,
        sx: {
          fontSize: isMobile ? '0.875rem' : '1rem',
          fontWeight: 400,
          padding: isMobile ? theme.spacing(1, 1.5) : theme.spacing(1.5, 2),
          backgroundColor: theme.palette.background.paper,
          borderRadius: theme.shape.borderRadius,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.02),
          },
          '&.Mui-focused': {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.background.paper,
            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
          },
        },
        startAdornment: (
          <InputAdornment position="start">
            <AddIcon 
              fontSize="small" 
              sx={{ 
                color: theme.palette.text.secondary,
                mr: 1,
              }} 
            />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.25 : 0.5 }}>
              {/* Quick actions - hide some on mobile */}
              {!isExpanded && !isMobile && (
                <Fade in>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {/* Assignee */}
                    <Tooltip title="Assign to">
                      <IconButton 
                        size="small"
                        onClick={() => setIsExpanded(true)}
                        sx={{ 
                          color: assigneeId ? theme.palette.primary.main : theme.palette.text.secondary,
                        }}
                      >
                        <PersonIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    {/* Due date */}
                    <Tooltip title="Set due date">
                      <IconButton 
                        size="small"
                        onClick={() => {
                          setIsExpanded(true);
                          setShowDatePicker(true);
                        }}
                        sx={{ 
                          color: dueDate ? theme.palette.primary.main : theme.palette.text.secondary,
                        }}
                      >
                        <CalendarIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    {/* Priority */}
                    <Tooltip title="Set priority">
                      <IconButton 
                        size="small"
                        onClick={() => setIsExpanded(true)}
                        sx={{ 
                          color: priority !== 'medium' ? (priorityColors[priority] || theme.palette.text.secondary) : theme.palette.text.secondary,
                        }}
                      >
                        <FlagIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Fade>
              )}
              
              {/* Expand/Collapse - always show on mobile */}
              <Tooltip title={isExpanded ? "Show less" : isMobile ? "Options" : "Show more options"}>
                <IconButton 
                  size={isMobile ? "small" : "small"}
                  onClick={() => setIsExpanded(!isExpanded)}
                  sx={{ 
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                    padding: isMobile ? '4px' : '8px',
                  }}
                >
                  <ExpandIcon fontSize={isMobile ? "small" : "small"} />
                </IconButton>
              </Tooltip>
              
              {/* Send button */}
              <Tooltip title="Create task (Enter)">
                <IconButton 
                  size={isMobile ? "small" : "small"}
                  onClick={handleCreate}
                  disabled={!title.trim() || isCreating}
                  sx={{ 
                    color: theme.palette.primary.main,
                    padding: isMobile ? '4px' : '8px',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  <SendIcon fontSize={isMobile ? "small" : "small"} />
                </IconButton>
              </Tooltip>
              
              {/* Close button (if closeable) */}
              {onClose && (
                <Tooltip title="Cancel (Esc)">
                  <IconButton 
                    size="small"
                    onClick={onClose}
                    sx={{ ml: 0.5 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </InputAdornment>
        ),
      }}
    />
  );
  
  // Expanded options
  const renderExpandedOptions = () => (
    <Collapse in={isExpanded}>
      <Box 
        sx={{ 
          mt: 1,
          p: isMobile ? 1.5 : 2,
          backgroundColor: alpha(theme.palette.background.paper, 0.5),
          borderRadius: theme.shape.borderRadius,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Grid container spacing={2}>
          {/* Assignee selector */}
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Assignee
              </Typography>
              <Button
                variant="outlined"
                size="small"
                fullWidth
                startIcon={<PersonIcon />}
                onClick={() => setAssigneeId(assigneeId ? null : user._id)}
                sx={{ 
                  justifyContent: 'flex-start',
                  color: assigneeId ? theme.palette.primary.main : theme.palette.text.secondary,
                }}
              >
                {assigneeId ? 'Me' : 'Unassigned'}
              </Button>
            </Box>
          </Grid>
          
          {/* Due date */}
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Due date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={dueDate}
                  onChange={setDueDate}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      size="small" 
                      fullWidth
                      placeholder="No due date"
                    />
                  )}
                  componentsProps={{
                    actionBar: {
                      actions: ['clear', 'today'],
                    },
                  }}
                />
              </LocalizationProvider>
              {!showDatePicker && (
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                  {quickDates.map((date) => (
                    <Chip
                      key={date.label}
                      label={date.label}
                      size="small"
                      onClick={() => setDueDate(date.value)}
                      variant={dueDate && format(dueDate, 'yyyy-MM-dd') === format(date.value, 'yyyy-MM-dd') ? 'filled' : 'outlined'}
                      sx={{ 
                        fontSize: isMobile ? '0.7rem' : '0.75rem',
                        height: isMobile ? 22 : 24,
                        cursor: 'pointer',
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Grid>
          
          {/* Priority */}
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Priority
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                {['low', 'medium', 'high', 'urgent'].map((p) => (
                  <Button
                    key={p}
                    size="small"
                    variant={priority === p ? 'contained' : 'outlined'}
                    onClick={() => setPriority(p)}
                    sx={{ 
                      minWidth: isMobile ? 0 : 0,
                      px: isMobile ? 1 : 1.5,
                      py: isMobile ? 0.5 : 1,
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                      flex: isMobile ? '1 1 45%' : 'none',
                      color: priority === p ? 'white' : priorityColors[p],
                      backgroundColor: priority === p ? priorityColors[p] : 'transparent',
                      borderColor: priorityColors[p],
                      '&:hover': {
                        backgroundColor: priority === p ? priorityColors[p] : alpha(priorityColors[p] || '#2196f3', 0.1),
                      },
                    }}
                  >
                    {isMobile ? p.charAt(0).toUpperCase() : p.charAt(0).toUpperCase() + p.slice(1, 3)}
                  </Button>
                ))}
              </Box>
            </Box>
          </Grid>
        </Grid>
        
        {/* Help text */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <EnterIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
          <Typography variant="caption" color="text.secondary">
            Press Enter to create task
          </Typography>
        </Box>
      </Box>
    </Collapse>
  );
  
  // Render based on variant
  const renderContent = () => {
    switch (variant) {
      case 'floating':
        return (
          <Box
            sx={{
              position: 'fixed',
              bottom: isMobile ? theme.spacing(2) : theme.spacing(3),
              left: isMobile ? theme.spacing(2) : 'auto',
              right: isMobile ? theme.spacing(2) : theme.spacing(3),
              zIndex: theme.zIndex.fab,
              backgroundColor: theme.palette.background.paper,
              borderRadius: theme.shape.borderRadius * 2,
              boxShadow: theme.shadows[8],
              p: isMobile ? 1.5 : 2,
              minWidth: isMobile ? 'auto' : 400,
              maxWidth: isMobile ? 'none' : 600,
              width: isMobile ? 'auto' : 'auto',
            }}
          >
            {renderMainInput()}
            {renderExpandedOptions()}
          </Box>
        );
        
      case 'minimal':
        return renderMainInput();
        
      default: // inline
        return (
          <Box sx={{ 
            width: '100%',
            maxWidth: isMobile ? '100%' : '800px',
            margin: '0 auto',
          }}>
            {renderMainInput()}
            {renderExpandedOptions()}
          </Box>
        );
    }
  };
  
  return variant === 'floating' ? (
    <ClickAwayListener onClickAway={() => onClose && onClose()}>
      {renderContent()}
    </ClickAwayListener>
  ) : (
    renderContent()
  );
};

export default SimpleQuickTaskCreate;